import { useRef, useMemo } from "react";
import { FolderIcon, CloudArrowUpIcon, DocumentMagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { StorageUploadRow } from "./StorageUploadRow";
import { toast } from "react-toastify";

const MAX_DEPTH = 10;

const StorageUploader = ({ formData = { name: "", documents: [] }, setFormData = () => {}, uploadType = "file" }) => {
  const inputFileRef = useRef(null);
  const folderInputRef = useRef(null);

  const hasFiles = formData.documents?.length > 0;

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files || []);
    if (selectedFiles.length === 0) return;

    const validFiles = [];
    let skippedCount = 0;

    selectedFiles.forEach((file) => {
      const path = uploadType === "folder" ? file.webkitRelativePath || "" : "";

      const depth = path.split("/").length - 1;

      if (depth > MAX_DEPTH) {
        skippedCount++;
        return;
      }

      validFiles.push({
        file,
        id: `${file.name}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        name: file.name.replace(/\.[^/.]+$/, ""),
        path: path,
      });
    });

    if (skippedCount > 0) {
      toast.warning(`${skippedCount} files were skipped (path too deep).`);
    }

    setFormData((prev) => ({
      ...prev,
      documents: [...(prev.documents || []), ...validFiles],
      folderNames: prev.folderNames || {},
    }));

    if (inputFileRef.current) inputFileRef.current.value = "";
    if (folderInputRef.current) folderInputRef.current.value = "";
  };

  const handleClearAll = () => {
    setFormData((prev) => ({ ...prev, documents: [], folderNames: {} }));
    if (inputFileRef.current) inputFileRef.current.value = "";
  };

  const updateFolderName = (fullPath, newName) => {
    setFormData((prev) => ({
      ...prev,
      folderNames: { ...prev.folderNames, [fullPath]: newName },
    }));
  };

  const groupedStructure = useMemo(() => {
    const structure = { files: [], folders: {} };
    (formData.documents || []).forEach((doc) => {
      if (!doc.path || !doc.path.includes("/")) {
        structure.files.push(doc);
      } else {
        const parts = doc.path.split("/");
        let current = structure;
        let currentPath = "";
        for (let i = 0; i < parts.length - 1; i++) {
          const segment = parts[i];
          currentPath = currentPath ? `${currentPath}/${segment}` : segment;
          if (!current.folders[segment]) {
            current.folders[segment] = { files: [], folders: {}, fullPath: currentPath };
          }
          current = current.folders[segment];
        }
        current.files.push(doc);
      }
    });
    return structure;
  }, [formData.documents]);

  const renderTree = (node, depth = 0) => (
    <div className="space-y-3">
      {Object.entries(node.folders).map(([name, folder]) => (
        <div
          key={folder.fullPath}
          className="group/folder ml-1">
          <div className="mb-2 flex items-center gap-3 py-1">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-amber-50 ring-1 ring-amber-100 transition-colors group-hover/folder:bg-amber-100">
              <FolderIcon className="h-4 w-4 fill-amber-500/10 text-amber-500" />
            </div>

            <div className="flex flex-1 flex-col">
              <input
                className="border-b-1 w-full border-l-0 border-r-0 border-t-0 border-transparent bg-transparent p-0 pb-0.5 text-sm font-bold text-gray-800 transition-all duration-300 ease-in-out focus:border-amber-500/50 focus:ring-0"
                value={formData.folderNames?.[folder.fullPath] || name}
                onChange={(e) => updateFolderName(folder.fullPath, e.target.value)}
                spellCheck={false}
                autoComplete="off"
              />
              <span className="text-[10px] font-medium uppercase tracking-tight text-gray-400">
                {folder.files.length} {folder.files.length === 1 ? "item" : "items"}
              </span>
            </div>
          </div>

          <div className="ml-4 space-y-3 border-l border-gray-100 pl-4">{renderTree(folder, depth + 1)}</div>
        </div>
      ))}

      {node.files.map((file) => (
        <div
          key={file.id}
          className="animate-fade-in">
          <StorageUploadRow
            doc={file}
            onNameChange={(newName) => {
              const updated = formData.documents.map((d) => (d.id === file.id ? { ...d, name: newName } : d));
              setFormData((prev) => ({ ...prev, documents: updated }));
            }}
            handleDelete={() =>
              setFormData((prev) => ({
                ...prev,
                documents: prev.documents.filter((d) => d.id !== file.id),
              }))
            }
          />
        </div>
      ))}
    </div>
  );

  return (
    <div className="animate-fade-in flex h-full max-h-[70vh] flex-col">
      <div className="z-20 flex-shrink-0 bg-white pb-6">
        <div
          className={`group relative flex flex-col items-center justify-center rounded-2xl border-2 border-dashed transition-all duration-300 
    ${hasFiles ? "border-blue-200 bg-blue-50/30 py-4 shadow-sm" : "border-gray-200 bg-gray-50/50 py-10 hover:border-highlightColor hover:bg-white hover:shadow-md"}`}>
          <div className="flex flex-col items-center gap-4">
            <CloudArrowUpIcon className="h-12 w-12 text-highlightColor" />
            <div className="text-center">
              <p className="text-base font-semibold text-gray-700">Select your assets</p>
              <p className="text-sm text-gray-500">Choose individual files or an entire directory</p>
            </div>

            <div className="mt-2 flex gap-3">
              <input
                type="file"
                ref={inputFileRef}
                onChange={handleFileChange}
                multiple
                className="hidden"
              />
              <input
                type="file"
                ref={folderInputRef}
                onChange={handleFileChange}
                webkitdirectory=""
                directory=""
                className="hidden"
              />

              {uploadType === "file" ? (
                <button
                  onClick={() => inputFileRef.current.click()}
                  className="flex items-center gap-2 rounded-lg bg-white px-4 py-2 text-sm font-bold text-gray-700 shadow-sm ring-1 ring-gray-200 hover:bg-gray-50">
                  <DocumentMagnifyingGlassIcon className="h-4 w-4" /> Select Files
                </button>
              ) : (
                <button
                  onClick={() => folderInputRef.current.click()}
                  className="flex items-center gap-2 rounded-lg bg-white px-4 py-2 text-sm font-bold text-gray-700 shadow-sm ring-1 ring-gray-200 hover:bg-gray-50">
                  <FolderIcon className="h-4 w-4" /> Select Folder
                </button>
              )}
            </div>
          </div>

          {!hasFiles && (
            <div className="mt-6 flex w-full max-w-md flex-col items-center border-t border-gray-100 pt-6">
              <div className="flex gap-6">
                <div className="flex flex-col items-center">
                  <span className="text-[10px] font-bold uppercase tracking-widest text-gray-400">Max File Size</span>
                  <span className="text-xs font-semibold text-gray-600">300MB</span>
                </div>
                <div className="h-8 w-[1px] bg-gray-100" />
                <div className="flex flex-col items-center">
                  <span className="text-[10px] font-bold uppercase tracking-widest text-gray-400">Total Limit</span>
                  <span className="text-xs font-semibold text-gray-600">10GB</span>
                </div>
                <div className="h-8 w-[1px] bg-gray-100" />
                <div className="flex flex-col items-center">
                  <span className="text-[10px] font-bold uppercase tracking-widest text-gray-400">Supported</span>
                  <span className="text-xs font-semibold text-gray-600">All Formats</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {hasFiles && (
        <div className="scrollbar-thin scrollbar-thumb-gray-100 flex-1 overflow-y-auto px-1 pr-4">
          <div className="sticky top-0 z-10 mb-4 flex items-center justify-between border-b border-gray-100 bg-white pb-2">
            <h3 className="text-[10px] font-bold uppercase tracking-widest text-gray-400">Queue • {formData.documents.length} Items</h3>
            <button
              onClick={handleClearAll}
              className="text-[10px] font-bold uppercase tracking-widest text-red-500 hover:text-red-600">
              Clear All
            </button>
          </div>

          <div className="pb-12">{renderTree(groupedStructure)}</div>
        </div>
      )}
    </div>
  );
};

export default StorageUploader;
